/* ==========================================================================
   UNIFIED LIVE MINI-CART CONTROLLER (MP-MINI-CART.JS)
   - 100% Identical behavior on Product Page, Homepage, and Collections
   - Pure single request (Zero race conditions, Zero disappearing products)
   - Default 1 quantity guarantee (Strict single addition, no 2-qty bugs)
   - Sequential flow: Adds item to cart FIRST -> Opens mini-cart AFTER
   - Fetches uncached /cart HTML
   - Zero-redirect & Zero-scroll
   ========================================================================== */

(function () {
  'use strict';

  var isCartBusy = false;
  var requestQueue = [];
  var lastActionTimestamp = 0;
  var userManuallyClosed = false;

  /* ── 0. CRITICAL CSS FIXES ── */
  (function injectStyles() {
    var styleId = 'mp-mini-cart-inline-fixes';
    if (document.getElementById(styleId)) return;

    var style = document.createElement('style');
    style.id = styleId;
    style.textContent = [
      '#mini-cart, #mini-cart.mini-cart, #mini-cart .mini-cart__inner {',
      '  background-color: #ffffff !important;',
      '  background: #ffffff !important;',
      '  opacity: 1 !important;',
      '}',
      'body.mini-cart-open #mini-cart,',
      '#mini-cart.is-open,',
      '#mini-cart[aria-hidden="false"] {',
      '  display: flex !important;',
      '  visibility: visible !important;',
      '  opacity: 1 !important;',
      '  pointer-events: auto !important;',
      '}',
      'body:not(.mini-cart-open) #mini-cart[aria-hidden="true"]:not(.is-open) {',
      '  display: none !important;',
      '  visibility: hidden !important;',
      '  opacity: 0 !important;',
      '  pointer-events: none !important;',
      '}',
      '.header [aria-controls*="menu"],',
      '.header [data-action*="menu"],',
      '.header [aria-controls*="sidebar"],',
      '.header [data-action="toggle-menu"],',
      '.header__icon-wrapper--menu,',
      '.header__nav-toggle,',
      '.header__mobile-nav-toggle {',
      '  transform: none !important;',
      '}',
      '.header [aria-controls*="menu"] svg,',
      '.header [data-action*="menu"] svg,',
      '.header [aria-controls*="sidebar"] svg,',
      '.header [data-action="toggle-menu"] svg,',
      '.header__icon-wrapper--menu svg,',
      '.header [aria-controls*="menu"] .icon-state__primary,',
      '.header [data-action*="menu"] .icon-state__primary,',
      '.header__nav-toggle .icon-state__primary,',
      '.header__mobile-nav-toggle .icon-state__primary {',
      '  transform: none !important;',
      '  opacity: 1 !important;',
      '  visibility: visible !important;',
      '  display: inline-flex !important;',
      '}',
      '.header [aria-controls*="menu"] .icon-state__secondary,',
      '.header [data-action*="menu"] .icon-state__secondary,',
      '.header__nav-toggle .icon-state__secondary,',
      '.header__mobile-nav-toggle .icon-state__secondary {',
      '  display: none !important;',
      '}',
      '.header__cart-icon, .header__cart-toggle, [data-action="toggle-mini-cart"] {',
      '  position: relative !important;',
      '}',
      '.header__cart-icon .icon-state__secondary,',
      '.header__cart-toggle .icon-state__secondary,',
      '[data-action="toggle-mini-cart"] .icon-state__secondary {',
      '  position: absolute !important;',
      '  top: 50% !important;',
      '  left: 50% !important;',
      '  margin: 0 !important;',
      '}',
      'body.mini-cart-open .header__cart-icon .icon-state__primary,',
      'body.mini-cart-open .header__cart-toggle .icon-state__primary,',
      'body.mini-cart-open [data-action="toggle-mini-cart"] .icon-state__primary {',
      '  opacity: 0 !important;',
      '  visibility: hidden !important;',
      '  pointer-events: none !important;',
      '  transform: scale(0.3) !important;',
      '}',
      'body.mini-cart-open .header__cart-icon .icon-state__secondary,',
      'body.mini-cart-open .header__cart-toggle .icon-state__secondary,',
      'body.mini-cart-open [data-action="toggle-mini-cart"] .icon-state__secondary {',
      '  display: block !important;',
      '  opacity: 1 !important;',
      '  visibility: visible !important;',
      '  pointer-events: auto !important;',
      '  transform: translate(-50%, -50%) scale(1) !important;',
      '}',
      'body:not(.mini-cart-open) .header__cart-icon .icon-state__primary,',
      'body:not(.mini-cart-open) .header__cart-toggle .icon-state__primary,',
      'body:not(.mini-cart-open) [data-action="toggle-mini-cart"] .icon-state__primary {',
      '  display: block !important;',
      '  opacity: 1 !important;',
      '  visibility: visible !important;',
      '  pointer-events: auto !important;',
      '  transform: none !important;',
      '}',
      'body:not(.mini-cart-open) .header__cart-icon .icon-state__secondary,',
      'body:not(.mini-cart-open) .header__cart-toggle .icon-state__secondary,',
      'body:not(.mini-cart-open) [data-action="toggle-mini-cart"] .icon-state__secondary {',
      '  opacity: 0 !important;',
      '  visibility: hidden !important;',
      '  pointer-events: none !important;',
      '  transform: translate(-50%, -50%) scale(0.3) !important;',
      '}',
      '[data-mp-variant-card], .mp-variant-card, .variant-card {',
      '  min-height: 56px !important;',
      '  height: 56px !important;',
      '  padding: 0 16px !important;',
      '  display: flex !important;',
      '  align-items: center !important;',
      '  justify-content: space-between !important;',
      '  box-sizing: border-box !important;',
      '}'
    ].join('\n');
    document.head.appendChild(style);
  })();

  /* ── 1. HELPER: CHECK IF MINI-CART IS OPEN ── */
  function isCartOpen() {
    var miniCart = document.getElementById('mini-cart') || document.querySelector('.mini-cart');
    if (miniCart) {
      if (miniCart.getAttribute('aria-hidden') === 'false' || miniCart.classList.contains('is-visible') || miniCart.classList.contains('is-open')) {
        return true;
      }
    }
    return document.body.classList.contains('mini-cart-open');
  }

  /* ── 2. SYNC ARIA AND BODY CLASSES ── */
  function syncCartState(isOpen) {
    var cartToggles = document.querySelectorAll('.header__cart-toggle, [data-action="toggle-mini-cart"], [aria-controls="mini-cart"]');
    if (isOpen) {
      document.body.classList.add('mini-cart-open');
      cartToggles.forEach(function (t) { t.setAttribute('aria-expanded', 'true'); });
    } else {
      document.body.classList.remove('mini-cart-open');
      cartToggles.forEach(function (t) { t.setAttribute('aria-expanded', 'false'); });
    }
  }

  /* ── 3. OPEN & CLOSE MINI-CART ── */
  function openMiniCart() {
    if (window.location.pathname === '/cart') return;
    userManuallyClosed = false;
    var miniCart = document.getElementById('mini-cart') || document.querySelector('.mini-cart');
    if (miniCart) {
      miniCart.setAttribute('aria-hidden', 'false');
      miniCart.classList.add('is-visible', 'is-open');
    }
    if (window.theme && window.theme.cart && typeof window.theme.cart._openMiniCart === 'function') {
      try { window.theme.cart._openMiniCart(); } catch (e) {}
    }
    syncCartState(true);
  }

  function closeMiniCart() {
    userManuallyClosed = true;
    var miniCart = document.getElementById('mini-cart') || document.querySelector('.mini-cart');
    if (miniCart) {
      miniCart.setAttribute('aria-hidden', 'true');
      miniCart.classList.remove('is-visible', 'is-open');
    }
    if (window.theme && window.theme.cart && typeof window.theme.cart._closeMiniCart === 'function') {
      try { window.theme.cart._closeMiniCart(); } catch (e) {}
    }
    syncCartState(false);
  }

  /* ── 4. UPDATE BADGE COUNT ── */
  function updateBadgeCount(count) {
    document.querySelectorAll('.header__cart-count, [data-cart-count], #mpCartCount').forEach(function (el) {
      el.textContent = count;
    });
  }

  /* ── 5. SINGLE-THREADED QUEUE ── */
  function enqueueCartTask(taskFn) {
    return new Promise(function (resolve, reject) {
      requestQueue.push({ fn: taskFn, resolve: resolve, reject: reject });
      runNextTask();
    });
  }

  function runNextTask() {
    if (isCartBusy || requestQueue.length === 0) return;
    isCartBusy = true;
    var current = requestQueue.shift();

    current.fn()
      .then(function (res) {
        current.resolve(res);
      })
      .catch(function (err) {
        current.reject(err);
      })
      .finally(function () {
        isCartBusy = false;
        runNextTask();
      });
  }

  /* ── 6. RE-RENDER MINI-CART (FROM UNCACHED /cart) & OPEN AFTER RENDER ── */
  window.openAndRefreshCart = function (shouldForceOpen) {
    if (shouldForceOpen !== false) {
      userManuallyClosed = false;
    }

    var miniCart = document.getElementById('mini-cart') || document.querySelector('.mini-cart');
    if (!miniCart) return Promise.resolve();

    return fetch('/cart?_t=' + Date.now(), {
      credentials: 'same-origin',
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache'
      }
    })
      .then(function (res) { return res.text(); })
      .then(function (html) {
        if (!html) return;
        var parser = new DOMParser();
        var doc = parser.parseFromString(html, 'text/html');
        var newCart = doc.getElementById('mini-cart') || doc.querySelector('.mini-cart');

        if (newCart && miniCart) {
          var currentList = miniCart.querySelector('.mini-cart__line-item-list, .mini-cart__content');
          var scrollPos = currentList ? currentList.scrollTop : 0;

          // 1. First inject the new product and HTML into the mini-cart
          miniCart.innerHTML = newCart.innerHTML;

          var newList = miniCart.querySelector('.mini-cart__line-item-list, .mini-cart__content');
          if (newList && scrollPos > 0) {
            newList.scrollTop = scrollPos;
          }

          var itemCount = newCart.getAttribute('data-item-count');
          if (itemCount !== null) {
            miniCart.setAttribute('data-item-count', itemCount);
            updateBadgeCount(parseInt(itemCount, 10) || 0);
          }
        }

        var newBadge = doc.querySelector('.header__cart-count');
        if (newBadge) {
          updateBadgeCount(parseInt(newBadge.textContent.trim(), 10) || 0);
        }

        // 2. Only AFTER product is in the cart and rendered in mini-cart, open it!
        if (shouldForceOpen !== false && !userManuallyClosed) {
          openMiniCart();
        }
      })
      .catch(function (err) {
        console.error('[MiniCart Sync Error]', err);
        if (shouldForceOpen !== false && !userManuallyClosed) {
          openMiniCart();
        }
      });
  };

  /* ── 7. ATOMIC ADD TO CART (STANDARDIZED FORMDATA & SEQUENTIAL OPEN) ── */
  window.mpAddToCart = function (formData, triggerBtn) {
    var btn = triggerBtn || null;
    var originalHTML = btn ? btn.innerHTML : '';

    if (btn) {
      btn.disabled = true;
      var label = btn.querySelector('.ap-btn-label') || btn;
      if (label) label.textContent = 'Adding…';
      btn.classList.add('ap-btn--loading');
    }

    return enqueueCartTask(function () {
      return fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      })
        .then(function (res) {
          if (!res.ok) {
            return res.json().then(function (data) {
              throw new Error(data.description || 'Add to cart failed');
            });
          }
          return res.json();
        })
        .then(function (addedItem) {
          if (btn) {
            btn.classList.remove('ap-btn--loading');
            btn.classList.add('ap-btn--success');
            var label = btn.querySelector('.ap-btn-label') || btn;
            if (label) label.textContent = 'Added ✓';
          }
          userManuallyClosed = false;

          document.dispatchEvent(new CustomEvent('theme:loading:end'));

          // Close any open Quick View modal
          document.dispatchEvent(new CustomEvent('modal:close'));
          var openModal = document.querySelector('.modal[aria-hidden="false"]');
          if (openModal) {
            openModal.setAttribute('aria-hidden', 'true');
          }

          // REFRESH MINI-CART WITH NEW ITEM FIRST, AND AFTER IT OPENS AUTOMATICALLY
          return window.openAndRefreshCart(true).then(function () {
            return addedItem;
          });
        })
        .catch(function (err) {
          console.error('[Add to Cart Error]', err);
          if (btn) {
            btn.classList.remove('ap-btn--loading');
            btn.classList.add('ap-btn--error');
            var label = btn.querySelector('.ap-btn-label') || btn;
            if (label) label.textContent = 'Try again';
          }
        })
        .finally(function () {
          if (btn) {
            setTimeout(function () {
              btn.disabled = false;
              btn.classList.remove('ap-btn--success', 'ap-btn--error');
              btn.innerHTML = originalHTML;
            }, 1800);
          }
        });
    });
  };

  /* ── 8. QUANTITY & REMOVE ACTIONS ── */
  function handleQuantityAction(line, newQty) {
    return enqueueCartTask(function () {
      return fetch('/cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ line: line, quantity: newQty })
      })
        .then(function (res) { return res.json(); })
        .then(function (cart) {
          updateBadgeCount(cart.item_count);
          return window.openAndRefreshCart(false);
        })
        .catch(function (err) {
          console.error('[Quantity Update Error]', err);
          return window.openAndRefreshCart(false);
        });
    });
  }

  /* ── 9. GLOBAL CLICK INTERCEPTOR ── */
  document.addEventListener('click', function (e) {
    var target = e.target;

    // A. "View Cart" button inside mini-cart (allow normal /cart navigation)
    var viewCartLink = target.closest('#mini-cart a[href*="/cart"], .mini-cart a[href*="/cart"]');
    if (viewCartLink) return;

    // B. Header Cart Toggle Icon
    var cartToggle = target.closest('.header__cart-toggle, [data-action="toggle-mini-cart"], [aria-controls="mini-cart"]');
    if (cartToggle) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      if (isCartOpen()) {
        closeMiniCart();
      } else {
        openMiniCart();
      }
      return;
    }

    // C. Quantity Plus / Minus in Mini Cart
    var qtyBtn = target.closest('#mini-cart [data-action="increase-quantity"], #mini-cart [data-action="decrease-quantity"]');
    if (qtyBtn) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      var line = parseInt(qtyBtn.getAttribute('data-line'), 10);
      var qty = parseInt(qtyBtn.getAttribute('data-quantity'), 10);
      if (!isNaN(line) && !isNaN(qty)) {
        handleQuantityAction(line, qty);
      }
      return;
    }

    // D. "Remove" item link in Mini Cart
    var removeLink = target.closest('#mini-cart .mini-cart__quantity-remove, #mini-cart a[href*="/cart/change"]');
    if (removeLink) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      var rLine = removeLink.getAttribute('data-line') || (new URL(removeLink.href, window.location.origin).searchParams.get('line'));
      if (rLine) {
        handleQuantityAction(parseInt(rLine, 10), 0);
      }
      return;
    }

    // E. ALL ADD TO CART BUTTONS (Homepage All-Products, Collection cards, Quick Add)
    var atcBtn = target.closest([
      '.ap-btn--cart',
      '[data-variant-id].ap-btn--cart',
      '[data-mp-add-to-cart]',
      '.mp__btn--atc',
      '[data-action="add-to-cart"]',
      '.product-item__action-button',
      'button[name="add"]',
      '.product-form__add-button',
      '[data-add-to-cart]'
    ].join(', '));

    if (atcBtn) {
      // Find form and card container on collection pages
      var form = atcBtn.closest('form[action*="/cart/add"]') || atcBtn.closest('form') || atcBtn.form;
      var card = atcBtn.closest('.product-item, .product-card, .grid__item, .card, [data-product-id]');
      if (!form && card) {
        form = card.querySelector('form[action*="/cart/add"], form');
      }

      var idInput = form ? form.querySelector('input[name="id"], select[name="id"]') : null;
      if (!idInput && card) {
        idInput = card.querySelector('input[name="id"], select[name="id"]');
      }

      var vid = atcBtn.getAttribute('data-variant-id') || 
                atcBtn.getAttribute('data-id') || 
                (atcBtn.name === 'id' ? atcBtn.value : null) || 
                (idInput ? idInput.value : '') || 
                atcBtn.value;

      // If button is a modal trigger ("Choose options" / "Quick view") without a variant ID, let theme open modal!
      if (!vid && (atcBtn.getAttribute('data-action') === 'open-modal' || atcBtn.hasAttribute('aria-controls') || atcBtn.tagName === 'A')) {
        return;
      }

      // STOP THE BROWSER AND ALL OTHER SCRIPTS (THEME.JS)
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      // STRICT LOCK TO PREVENT DUPLICATE / PARALLEL ADDS (PREVENTS 2 QUANTITY BUG)
      if (isCartBusy) return;
      var now = Date.now();
      if (now - lastActionTimestamp < 800) return;
      lastActionTimestamp = now;

      // Disable button immediately so physical double taps can't fire twice
      atcBtn.disabled = true;

      var formData = form ? new FormData(form) : new FormData();
      if (vid) {
        formData.set('id', vid);
      }

      // STRICT DEFAULT 1 QUANTITY GUARANTEE
      var qtyInput = form ? form.querySelector('input[name="quantity"]') : null;
      var qtyVal = 1;
      if (qtyInput && parseInt(qtyInput.value, 10) > 0) {
        qtyVal = parseInt(qtyInput.value, 10);
      }
      formData.set('quantity', String(qtyVal));

      window.mpAddToCart(formData, atcBtn);
      return;
    }

    // F. Click Outside to Close
    if (isCartOpen()) {
      var miniCartEl = document.getElementById('mini-cart') || document.querySelector('.mini-cart');
      var isInsideCart = miniCartEl && miniCartEl.contains(target);
      var isToggle = target.closest('.header__cart-toggle, [data-action="toggle-mini-cart"]');

      if (!isInsideCart && !isToggle) {
        closeMiniCart();
      }
    }
  }, true);

  /* ── 10. GLOBAL FORM SUBMIT INTERCEPTOR ── */
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form) return;

    if (form.id === 'mini-cart' || form.classList.contains('mini-cart') || form.closest('#mini-cart')) {
      return;
    }

    var action = form.getAttribute('action') || '';
    if (action.indexOf('/cart/add') !== -1 || form.classList.contains('shopify-product-form')) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      if (isCartBusy) return;
      var now = Date.now();
      if (now - lastActionTimestamp < 800) return;
      lastActionTimestamp = now;

      var submitBtn = form.querySelector('[type="submit"], [name="add"], .product-form__add-button');
      if (submitBtn) submitBtn.disabled = true;

      var formData = new FormData(form);
      var qtyInput = form.querySelector('input[name="quantity"]');
      var qtyVal = (qtyInput && parseInt(qtyInput.value, 10) > 0) ? parseInt(qtyInput.value, 10) : 1;
      formData.set('quantity', String(qtyVal));

      window.mpAddToCart(formData, submitBtn);
    }
  }, true);

  /* ── 11. WHEEL SCROLL PASSTHROUGH ── */
  document.addEventListener('wheel', function (e) {
    var miniCart = document.getElementById('mini-cart') || document.querySelector('.mini-cart');
    if (!miniCart || !isCartOpen()) return;

    if (miniCart.contains(e.target)) {
      var scroller = miniCart.querySelector('.mini-cart__content, .mini-cart__line-item-list');
      if (scroller && scroller.scrollHeight > scroller.clientHeight) {
        scroller.scrollTop += e.deltaY;
        e.preventDefault();
      }
    }
  }, { passive: false });

  /* ── 12. STORE-WIDE THEME EVENT & FETCH BACKUP ── */
  ['cart:updated', 'cart:item-added', 'product:added', 'theme:cart:change'].forEach(function (eventName) {
    document.addEventListener(eventName, function () {
      if (isCartBusy) return;
      userManuallyClosed = false;
      window.openAndRefreshCart(true);
    });
  });

  var originalFetch = window.fetch;
  window.fetch = function () {
    var url = arguments[0];
    var isCartAdd = false;
    if (typeof url === 'string') {
      isCartAdd = url.indexOf('/cart/add') !== -1;
    } else if (url && typeof url === 'object' && url.url) {
      isCartAdd = url.url.indexOf('/cart/add') !== -1;
    }

    return originalFetch.apply(this, arguments).then(function (res) {
      if (isCartAdd && res.ok && !isCartBusy) {
        userManuallyClosed = false;
        // Do not open immediately; refresh cart and open AFTER content is updated!
        setTimeout(function () {
          window.openAndRefreshCart(true);
        }, 50);
      }
      return res;
    });
  };

})();